import type { NodeStyle } from '../nodes/NodeStyle';
import type {
  ReportId,
  ReportSeverity,
  ReportSeverityDescriptions,
  ReportSeverityResults,
} from '../report';

export const NON_REDUNDANT_EDGES_INTENT_RULE = 'nonRedundantEdges';
export const SINGLE_POINTS_OF_FAILURE_INTENT_RULE = 'singlePointsOfFailure';

export const builtInIntentRules = [
  NON_REDUNDANT_EDGES_INTENT_RULE,
  SINGLE_POINTS_OF_FAILURE_INTENT_RULE,
] as const;

export type BuiltInIntentRule = (typeof builtInIntentRules)[number];

export type IntentRuleId = ReportId | BuiltInIntentRule;

export function isBuiltInIntentRule(rule: string): rule is BuiltInIntentRule {
  return (builtInIntentRules as readonly string[]).includes(rule);
}

export type GraphIntentVerificationRuleType = 'builtInRules' | 'definedRules';

export type BinarySeverityResults = Pick<ReportSeverityResults, 0 | 30>;

export interface IntentVerificationListRecord<TResults> {
  id: string;
  name: string;
  results: TResults;
}

export interface IntentVerificationListRecordDescribed<
  TResults,
  TResultsDescriptions,
> extends IntentVerificationListRecord<TResults> {
  descriptions: {
    checks: TResultsDescriptions;
    general: string;
  };
}

export interface IntentVerificationListGroup<TResults, TResultsDescriptions>
  extends IntentVerificationListRecord<TResults> {
  children: IntentVerificationListRecordDescribed<
    TResults,
    TResultsDescriptions
  >[];
}

export interface GraphIntentVerificationBadge {
  count: number;
  severity: ReportSeverity;
}

interface GraphIntentVerificationRuleSummary<TList> {
  badge: GraphIntentVerificationBadge;
  list: TList[];
}

export type BuiltInIntentVerificationRuleSummary =
  GraphIntentVerificationRuleSummary<
    IntentVerificationListRecord<BinarySeverityResults>
  >;
export type DefinedIntentVerificationRuleSummary =
  GraphIntentVerificationRuleSummary<
    IntentVerificationListGroup<
      ReportSeverityResults,
      ReportSeverityDescriptions
    >
  >;

export type GraphIntentSummary = {
  builtInRules?: BuiltInIntentVerificationRuleSummary; // not present for path lookup or when requested definedRules only
  definedRules?: DefinedIntentVerificationRuleSummary; // not present when requested builtInRules only
};

export const intentSeverityToNodeStyle = {
  none: { background: 'gray' },
  0: { background: 'green' },
  10: { background: 'blueDark' },
  20: { background: 'orange' },
  30: { background: 'red' },
} as const;

export const getIntentResultNodeStyle = (
  severity: ReportSeverity | null,
): NodeStyle =>
  severity !== null
    ? intentSeverityToNodeStyle[severity]
    : intentSeverityToNodeStyle.none;
